/* Whoop-style ring tiles for the mobile home page.
   3 circular progress rings with center counts, in LexIQ palette. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import { listClients, listDocuments, listSessions } from "../../lib/api";

const CARD =
  "bg-white/55 backdrop-blur-md rounded-[28px] border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)]";

const ACHIEVE_KEY = "lexiq.ring_achievements.v1";
const LOG_KEY = "lexiq.achievements_log.v1";

/* Highest milestone strictly passed by `count` (0 if none yet). */
function highestPassed(count) {
  let last = 0;
  for (const m of MILESTONES) {
    if (count >= m) last = m;
    else break;
  }
  return last;
}

function readStored() {
  try { return JSON.parse(localStorage.getItem(ACHIEVE_KEY) || "{}"); }
  catch { return {}; }
}
function writeStored(v) {
  try { localStorage.setItem(ACHIEVE_KEY, JSON.stringify(v)); }
  catch { /* ignore quota errors */ }
}

function readLog() {
  try { return JSON.parse(localStorage.getItem(LOG_KEY) || "{}"); }
  catch { return {}; }
}
function writeLog(v) {
  try { localStorage.setItem(LOG_KEY, JSON.stringify(v)); }
  catch { /* ignore quota errors */ }
}

/* Append any newly-passed milestones to the log with timestamps. */
function logNewMilestones(counts) {
  const log = readLog();
  let changed = false;
  for (const key of Object.keys(counts)) {
    const passed = highestPassed(counts[key]);
    if (!passed) continue;
    const list = log[key] || [];
    const have = new Set(list.map((x) => x.level));
    for (const m of MILESTONES) {
      if (m > passed) break;
      if (!have.has(m)) {
        list.push({ level: m, at: new Date().toISOString() });
        changed = true;
      }
    }
    log[key] = list;
  }
  if (changed) writeLog(log);
  return log;
}

/* Per-tile config — vivid LexIQ palette + a complementary emerald for
   the legal-questions tile. Milestones grow dynamically — see `nextMilestone`. */
const MILESTONES = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];

/* Smallest milestone strictly greater than the current count.
   Falls back to the largest if the user already passed all of them. */
function nextMilestone(count) {
  for (const m of MILESTONES) if (count < m) return m;
  return MILESTONES[MILESTONES.length - 1];
}

const TILES = [
  {
    key: "clients",
    label: "العملاء",
    href: "/clients",
    testid: "tile-clients",
    color: "#3B82F6", // blue-500 — bright sapphire
  },
  {
    key: "documents",
    label: "الوثائق",
    href: "/draft",
    testid: "tile-documents",
    color: "#10B981", // emerald-500 — fresh bright green
  },
  {
    key: "chat",
    label: "الأسئلة",
    href: "/chat",
    testid: "tile-chat",
    color: "#F59E0B", // amber-500 — vivid honey gold
  },
];

function RingTile({ tile, count, achievement, onDismiss }) {
  const r = 40;
  const C = 2 * Math.PI * r;
  const milestone = nextMilestone(count);
  const fill = Math.min(count / milestone, 1);
  const dash = C * fill;
  const gap = C - dash;
  /* اضبط حجم العدد بحسب عدد الأرقام كي لا يتجاوز الدائرة */
  const numLen = String(count).length;
  const fontSize = numLen >= 4 ? 26 : numLen === 3 ? 30 : 34;
  return (
    <Link
      to={tile.href}
      data-testid={tile.testid}
      className={`${CARD} p-4 aspect-[0.88] flex flex-col items-center justify-center text-center active:scale-95 transition-transform`}
    >
      <div className="relative w-[100px] h-[100px]">
        {/* شارة "إنجاز جديد!" تَظهر عند تَجاوز milestone للمرّة الأولى */}
        {achievement && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
            data-testid={`${tile.key}-achievement`}
            className="absolute -top-1 -end-1 z-10 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold text-[#0A192F] shadow-md ring-2 ring-white"
            style={{
              background: "linear-gradient(135deg, #FFE7A6 0%, #E6C463 45%, #B8860B 100%)",
              animation: "lexiq-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
            aria-label="إنجاز جديد — اضغط للإخفاء"
            title="إنجاز جديد! اضغط للإخفاء"
          >
            <Sparkle size={10} weight="fill" />
            <span>إنجاز!</span>
          </button>
        )}
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Track + filled arc are drawn rotated -90° so the arc starts at
              the top, but the centered number stays UPRIGHT and on-axis
              by living inside an unrotated <g>. */}
          <g transform="rotate(-90 50 50)">
            <circle
              cx="50" cy="50" r={r}
              stroke={tile.color} strokeOpacity="0.18" strokeWidth="14" fill="none"
            />
            <circle
              cx="50" cy="50" r={r}
              stroke={tile.color} strokeWidth="14" fill="none"
              strokeDasharray={`${dash} ${gap}`}
              strokeLinecap="round"
              style={{ transition: "stroke-dasharray 600ms ease-out" }}
            />
          </g>
          <text
            x="50" y="50"
            textAnchor="middle"
            dy="0.35em"
            fontSize={fontSize}
            fontWeight="700"
            fontFamily="Tajawal, system-ui, sans-serif"
            fill="#000000"
            data-testid={`${tile.key}-count`}
          >
            {count}
          </text>
        </svg>
      </div>
      <p className="mt-2.5 text-[18px] font-bold text-[#0A192F]">
        {tile.label}
      </p>
    </Link>
  );
}

export default function RingTiles() {
  const [counts, setCounts] = useState({ clients: 0, documents: 0, chat: 0 });
  const [achievements, setAchievements] = useState({});

  useEffect(() => {
    let alive = true;
    const safe = (p) => p.then((arr) => (Array.isArray(arr) ? arr.length : 0)).catch(() => 0);
    Promise.all([safe(listClients()), safe(listDocuments()), safe(listSessions())]).then(
      ([clients, documents, chat]) => {
        if (!alive) return;
        const next = { clients, documents, chat };
        setCounts(next);
        // سجّل الإنجازات الجديدة في log بتاريخها
        logNewMilestones(next);
        // اكتَشف الإنجازات الجديدة (للشارة)
        const stored = readStored();
        const fresh = {};
        for (const key of Object.keys(next)) {
          const passed = highestPassed(next[key]);
          if (passed > 0 && passed > (stored[key] || 0)) fresh[key] = passed;
        }
        if (Object.keys(fresh).length) setAchievements(fresh);
      },
    );
    return () => { alive = false; };
  }, []);

  const dismiss = (key) => {
    setAchievements((prev) => {
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
    // اِحفظ كي لا تَظهر مرّة أخرى لنفس milestone
    const stored = readStored();
    stored[key] = highestPassed(counts[key]);
    writeStored(stored);
  };

  return (
    <section className="grid grid-cols-3 gap-2.5 mb-3" data-testid="ring-tiles">
      {TILES.map((t) => (
        <RingTile
          key={t.key}
          tile={t}
          count={counts[t.key]}
          achievement={achievements[t.key]}
          onDismiss={() => dismiss(t.key)}
        />
      ))}
    </section>
  );
}
